/**
 * The rail — §4 of the plan. Three cards, three audiences.
 *
 * **Quote total** is the customer's view of the money, drawn internally so
 * nobody has to open the proposal to check what was promised. Every optional
 * visit, service and line is a row of its own, so "If all options taken" is
 * a sum of things somebody can see.
 *
 * **Margin** is the opposite: everything the customer must never see, in one
 * place, so "is this worth doing" is one glance and not a mental subtraction.
 *
 * **Before you send** is the checklist, and the thing that decides whether
 * Send is enabled. Its rows come from `quoteChecks()` on the server, so the
 * rail and the send endpoint cannot disagree about whether a quote is ready.
 *
 * **Prices & costs** in the toolbar shows and hides the whole rail. What the
 * customer sees is the Proposal tab, never this one with columns taken away.
 */
import { Fragment, useState } from 'react'
import { visitExtras } from '@rowted/shared'
import type {
  MarginScope, QuoteCheck, QuoteMargin, QuoteTotals, QuoteVisitView,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDollars } from '../lib/format.js'
import { IconCheck, IconEye, IconTag, IconWarn } from '../frame/icons.js'
import { MarginPanel } from './margin-panel.js'
import {
  ALL_CLEAR, BASE_PRICE, BASE_PRICE_SUB, BEFORE_YOU_SEND, BREAKDOWN_LABELS, CUSTOMER_SEES_CHIP,
  IF_ALL_OPTIONS, NOTHING_OPTIONAL, OPTIONAL_NOT_IN_BASE, QUOTE_TOTAL_TITLE, REVIEW, FIX,
  SCOPE_HELP, SCOPE_HELP_PLANS, SCOPE_LABELS, TO_CHECK,
  CHOICES_VISITS, EARNS_LESS, IF_TOP_CHOICE, IN_BASE, LOWEST_CHOICE, PICKS_ONE_SECTION,
  UPGRADE_ADDS, UPGRADES_EARN_MORE,
} from '../builder/copy.js'
import { SectionLabel } from '../components/primitives.js'
import { MenuSelect } from '../components/menu-select.js'

export function QuoteTotalCard({
  totals, visits, onOpenVisit, title = QUOTE_TOTAL_TITLE, baseLabel = BASE_PRICE, topChoice = true,
  groupsWithChoices,
}: {
  /**
   * The questions that have choices of their own. A visit in one of those that
   * no question in `byChoice` covers is in a box and in no choice (step 22
   * item 2.2): it is in no price, so it is no row here either. Without this
   * the rail fell back to the pre-0115 rule and listed it "(selected)".
   */
  groupsWithChoices?: ReadonlySet<string>
  /** "If top choice taken" under a question — Board 5's package rail draws none. */
  topChoice?: boolean
  totals: QuoteTotals
  visits: QuoteVisitView[]
  onOpenVisit: (id: string) => void
  /** The card's eyebrow — the package page's reads "Price at the sample" (cb-05). */
  title?: string
  /** The line over the big number — "Base price · 6,000 sq ft" on a package. */
  baseLabel?: string
}) {
  /*
   * Two lists, because they are two different kinds of money (qb-07 row 2):
   * what is in the base price, and what the customer may add. An optional
   * visit used to be a row in the main list with a small "optional" after it,
   * and $33,099 sat in a column of figures that add up to the price.
   *
   * Choose-one groups collapse to one row. "Patio · Option A (selected)" is
   * what a customer will see once they have chosen, and listing both options
   * as two amounts that both count would describe a quote nobody sent.
   */
  const base: { id: string; visitId: string; label: string; amount: number }[] = []
  const optional: { id: string; visitId: string; label: string; amount: number }[] = []
  const seenGroups = new Set<string>()

  /*
   * A question is its own section (§9.3): one row per choice, the floor "in
   * base" and each upgrade by what it adds over it. The visits that answer
   * it are not base rows — Silver's five trips are the $480, not a sixth line.
   */
  const questions = totals.margin.byChoice
  for (const visit of visits) {
    if (visit.chooseGroupId) {
      if (seenGroups.has(visit.chooseGroupId)) continue
      seenGroups.add(visit.chooseGroupId)
      if (questions.some((question) => question.groupId === visit.chooseGroupId)) {
        visits.filter((other) => other.chooseGroupId === visit.chooseGroupId).forEach(extrasOf)
        continue
      }
      if (groupsWithChoices?.has(visit.chooseGroupId)) continue
      const group = visits.filter((other) => other.chooseGroupId === visit.chooseGroupId)
      const picked = group.find((other) => !other.optional) ?? group[0]!
      base.push({ id: picked.id, visitId: picked.id, label: `${picked.name} (selected)`, amount: picked.total })
      group.forEach(extrasOf)
      continue
    }
    const row = {
      id: visit.id,
      visitId: visit.id,
      label: `${visit.name}${visit.openEnded
        ? ' · while on rent'
        : visit.occurrences > 1 ? ` · ${visit.occurrences} visits` : ''}`,
      amount: visit.total,
    }
    if (visit.optional) optional.push(row)
    else base.push(row)
    extrasOf(visit)
  }

  /*
   * What may be added inside a visit — an optional service, or an optional
   * line on a service being bought anyway. `visitExtras` is the same terms
   * `options_total` is summed on, so these rows and "If all options taken"
   * cannot disagree.
   */
  function extrasOf(visit: QuoteVisitView) {
    for (const extra of visitExtras(visit)) {
      optional.push({ id: extra.id, visitId: visit.id, label: extra.name, amount: extra.amount })
    }
  }

  return (
    <Card>
      <div className="mb-2.5 flex items-center gap-2">
        <SectionLabel>{title}</SectionLabel>
        <span className="ml-auto inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-[6px]
                         border border-line bg-surface px-2 text-[11px] font-semibold text-text-muted">
          <IconEye size={12} /> {CUSTOMER_SEES_CHIP}
        </span>
      </div>

      {/* The number the customer is quoted, first and big (qb-07 row 1). */}
      <span className="text-[12.5px] text-text-muted">{baseLabel}</span>
      <div className="flex items-baseline gap-2">
        <b className="tnum text-[24px] font-bold leading-tight text-text">{formatDollars(totals.base)}</b>
        <span className="text-[12px] text-text-muted">
          {questions.length === 1 && base.length === 0
            ? LOWEST_CHOICE(questions[0]!.options.find((option) => option.inBase)?.name ?? '')
            : BASE_PRICE_SUB}
        </span>
      </div>

      {questions.map((question) => {
        const floor = question.options.find((option) => option.inBase)
        return (
          <div key={question.groupId} data-plan-section="" className="mt-3">
            <span className="mb-1 flex items-center gap-1.5"
              style={{ color: 'rgb(var(--status-quoteLine-deployment-fg))' }}>
              <IconTag size={12} />
              <SectionLabel className="!text-inherit">{PICKS_ONE_SECTION(question.groupName)}</SectionLabel>
            </span>
            {question.options.map((option) => (
              <div key={option.id} className="flex items-center gap-2 border-t border-line-faint py-1.5 text-[13px]">
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="truncate font-semibold text-text">{option.name}</b>
                  <span className="text-[11.5px] text-text-muted">{CHOICES_VISITS(option.visits)}</span>
                </span>
                <span className={cn('inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-[6px] px-[7px] text-[11px] font-semibold',
                  option.inBase ? 'bg-accent-soft text-accent-dark' : 'bg-info-bg text-info')}>
                  {option.inBase ? IN_BASE : `+ ${formatDollars(option.sold - (floor?.sold ?? 0))}`}
                </span>
                <b className="tnum w-[58px] shrink-0 text-right text-text">{formatDollars(option.sold)}</b>
              </div>
            ))}
          </div>
        )
      })}

      {(base.length > 0 || totals.recurring.length > 0) && (
      <dl className="m-0 mt-3 flex flex-col gap-1.5 border-t border-line-soft pt-3">
        {base.map((row) => (
          <div key={row.id} className="flex items-baseline gap-2">
            <button type="button" onClick={() => onOpenVisit(row.visitId)}
              className="min-w-0 flex-1 truncate text-left text-body text-text hover:underline">
              {row.label}
            </button>
            <span className="tnum shrink-0 text-body text-text">{formatDollars(row.amount)}</span>
          </div>
        ))}
        {/*
          * What repeats, under what totals. "$360 one-time + $480 / week while
          * on rent" is the offer; adding the second into the first would be
          * quoting a number of weeks nobody has agreed to.
          */}
        {totals.recurring.map((amount) => (
          <Line
            key={`${amount.period}-${amount.label}`}
            label={`Then ${amount.label}`}
            value={`+ ${formatDollars(amount.amount)}`}
            strong
          />
        ))}
      </dl>
      )}

      {optional.length > 0 && (
        <div className="mt-3 border-t border-line-soft pt-3">
          <SectionLabel className="mb-2 block">{OPTIONAL_NOT_IN_BASE}</SectionLabel>
          <dl className="m-0 flex flex-col gap-1.5">
            {optional.map((row) => (
              <div key={row.id} className="flex items-baseline gap-2">
                <button type="button" onClick={() => onOpenVisit(row.visitId)}
                  className="min-w-0 flex-1 truncate text-left text-body text-info hover:underline">
                  + {row.label}
                </button>
                <span className="tnum shrink-0 text-body text-info">{formatDollars(row.amount)}</span>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/*
        * No Tax row. Tax is worked out on every change and shown only on the
        * proposal, the approve screen and the invoice (qb-07 row 10,
        * catalog-units-and-buying §9.1).
        */}
      {optional.length > 0 && totals.options > 0 && (
        <div className="mt-3 border-t border-line-soft pt-2.5">
          <Line label={IF_ALL_OPTIONS} value={formatDollars(totals.base + totals.options)} muted />
        </div>
      )}
      {topChoice && optional.length === 0 && questions.length > 0 && (
        <div className="mt-2 border-t border-line-soft pt-2">
          <Line label={IF_TOP_CHOICE} value={formatDollars(totals.base + questions.reduce((sum, question) => {
            const floor = question.options.find((option) => option.inBase)?.sold ?? 0
            const top = Math.max(...question.options.map((option) => option.sold))
            return sum + (top - floor)
          }, 0))} muted />
        </div>
      )}
    </Card>
  )
}

/**
 * The quote's margin: Base · Options · All, one bar, one table and the
 * breakdown (folder 44 §6, §7.4) — `MarginPanel` on the internal tint.
 *
 * `scope` may be held by the page so "Open Margin ›" in a visit's popover can
 * scroll here and set it; left to itself the card keeps its own.
 */
export function MarginCard({
  margin, onOpenVisit, onFixCosts, hasSubs, scope: heldScope, onScope, id, note,
}: {
  margin: QuoteMargin
  /** A sentence at the foot saying where the figures came from (the package page's). */
  note?: string
  onOpenVisit?: (visitId: string) => void
  onFixCosts?: (lineId: string) => void
  hasSubs?: boolean
  scope?: MarginScope
  onScope?: (scope: MarginScope) => void
  id?: string
}) {
  const [ownScope, setOwnScope] = useState<MarginScope>('base')
  const scope = heldScope ?? ownScope
  const setScope = onScope ?? setOwnScope
  const choices = margin.byChoice.length > 0
  /* With a question, By choice first and By visit second (§9.3); without, visit and division. */
  const [picked, setMode] = useState<'visit' | 'division' | 'choice' | null>(null)
  const mode = picked ?? (choices ? 'choice' : 'visit')
  void onOpenVisit

  const optionsEmpty = margin.scopes.options.sold === 0 && margin.scopes.options.cost === 0 && !choices
  const question = margin.byChoice[0]
  const floor = question?.options.find((option) => option.inBase)?.name
  const upper = question?.options.filter((option) => !option.inBase).map((option) => option.name)
  const help = optionsEmpty
    ? NOTHING_OPTIONAL
    : scope === 'options' && floor && upper && upper.length > 0
      ? SCOPE_HELP_PLANS.options(upper.join(' and '), floor)
      : scope === 'all' && choices ? SCOPE_HELP_PLANS.all : SCOPE_HELP[scope]

  const rows = mode === 'division'
    ? margin.byDivision[scope]
    : mode === 'choice'
      ? margin.byChoice.flatMap((group) => group.options)
      : margin.byVisit[scope]

  return (
    <RailCard internal id={id}>
      <MarginPanel
        title
        block={margin.scopes[scope]}
        targetPct={margin.targetPct}
        density="rail"
        scope={{
          label: 'Margin scope',
          value: scope,
          onChange: (value) => setScope(value as MarginScope),
          help,
          options: (['base', 'options', 'all'] as const).map((value) => ({
            value, label: SCOPE_LABELS[value], disabled: value !== 'base' && optionsEmpty,
          })),
        }}
        breakdown={{
          rows,
          mode,
          onMode: (value) => setMode(value as typeof mode),
          modes: choices
            ? [
              { value: 'choice', label: BREAKDOWN_LABELS.choice },
              { value: 'visit', label: BREAKDOWN_LABELS.visit },
            ]
            : [
              { value: 'visit', label: BREAKDOWN_LABELS.visit },
              { value: 'division', label: BREAKDOWN_LABELS.division },
            ],
          after: mode === 'choice' && <UpgradeNote margin={margin} />,
        }}
        missingCost={{
          lines: margin.missingCost.lines,
          ...(margin.missingCost.firstLineId && onFixCosts
            ? { onFix: () => onFixCosts(margin.missingCost.firstLineId!) }
            : {}),
        }}
        notCounted={margin.notCounted}
        hasSubs={hasSubs}
      />
      {note && <p className="m-0 mt-3 text-[12px] leading-[1.45] text-text-muted">{note}</p>}
    </RailCard>
  )
}

/**
 * "Each upgrade adds: Gold +$240 at 41.7% · Platinum +$240 at 47.9%. The
 * upgrades earn more than the base." (§9.3) — from `byChoice[].options[].adds*`,
 * the server's figures; the last sentence names the first upgrade that earns
 * less than the floor, in amber, when one does.
 */
function UpgradeNote({ margin }: { margin: QuoteMargin }) {
  const notes = margin.byChoice.map((question) => {
    const floor = question.options.find((option) => option.inBase)
    const ups = question.options.filter((option) => !option.inBase)
    if (!floor || ups.length === 0) return null
    const weaker = ups.find((option) => option.addsMarginPct !== null && floor.marginPct !== null
      && option.addsMarginPct < floor.marginPct)
    return (
      <p key={question.groupId} data-upgrade-note=""
        className="m-0 mt-1.5 rounded-[9px] border border-line-soft bg-surface px-2.5 py-2 text-[12px] leading-[1.45] text-text-muted">
        {UPGRADE_ADDS}{' '}
        {ups.map((option, index) => (
          <Fragment key={option.id}>
            {index > 0 && ' · '}
            <b className="text-text">
              {option.name} +{formatDollars(option.addsSold)} at {option.addsMarginPct === null
                ? '—' : `${option.addsMarginPct.toFixed(1)}%`}
            </b>
          </Fragment>
        ))}
        .{' '}
        {weaker
          ? <span className="text-warn">{EARNS_LESS(weaker.name)}</span>
          : UPGRADES_EARN_MORE}
      </p>
    )
  })
  return <>{notes}</>
}

/**
 * Which terms this offer carries.
 *
 * Beside "Before you send" rather than inside the builder, because it is the
 * one thing on this screen that is about the agreement rather than the work —
 * and it is the last thing somebody checks before pressing Send, which is
 * where the rest of the rail already lives.
 */
export function TermsCard({
  termsId, termsName, choices, canEdit, onChange,
}: {
  termsId: string | null
  termsName: string | null
  choices: { id: string; name: string; isDefault: boolean }[]
  canEdit: boolean
  onChange: (termsId: string | null) => void
}) {
  // Nothing written and nothing carried: the catalog has the answer, and a
  // card offering an empty menu is a card in the way.
  if (choices.length === 0 && !termsId) return null

  return (
    <Card>
      <SectionLabel className="mb-2.5 block">Terms of service</SectionLabel>
      {canEdit
        ? (
          <MenuSelect
            size="field"
            label="Terms of service"
            value={termsId}
            onChange={onChange}
            clearable
            clearLabel="None"
            placeholder="None"
            options={choices.map((entry) => ({
              value: entry.id,
              label: entry.isDefault ? `${entry.name} · default` : entry.name,
            }))}
          />
        )
        : (
          <p className="m-0 text-body text-text">{termsName ?? 'None'}</p>
        )}
      <p className="m-0 mt-2 text-sub text-text-faint">
        {termsId
          ? 'Sent with the proposal, and frozen onto the approval when it is signed.'
          : 'This offer goes out with no conditions attached.'}
      </p>
    </Card>
  )
}

export function BeforeYouSendCard({
  checks, onGo, onReview, allClear,
}: {
  checks: QuoteCheck[]
  onGo: (check: QuoteCheck) => void
  /** Board 11 draws `✓ All clear` in the chip's place when nothing needs you (the Billing tab's card). */
  allClear?: boolean
  /** A margin row's Review › opens that visit's margin popover (qb-07 row 8). */
  onReview?: (visitId: string, anchor: HTMLElement) => void
}) {
  /*
   * Rows needing you come first; finished rows go quiet at the foot. "1 to
   * check" is the glance, so it is the card's chip rather than a sentence.
   */
  const needs = (check: QuoteCheck) => check.level === 'warn' || check.level === 'blocking'
  const sorted = [...checks.filter(needs), ...checks.filter((check) => !needs(check))]
  const count = checks.filter(needs).length

  return (
    <Card>
      <div className="mb-2.5 flex items-center gap-2">
        <SectionLabel>{BEFORE_YOU_SEND}</SectionLabel>
        {count > 0 && (
          <span className="ml-auto inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-[6px]
                           bg-warn-bg px-2 text-[11.5px] font-semibold text-warn">
            <IconWarn size={12} /> {TO_CHECK(count)}
          </span>
        )}
        {count === 0 && allClear && (
          <span className="ml-auto inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-[6px]
                           bg-accent-soft px-[7px] text-[11px] font-semibold text-accent-dark">
            <IconCheck size={11} /> {ALL_CLEAR}
          </span>
        )}
      </div>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {sorted.map((check) => (
          <li key={check.key} className="flex items-start gap-2">
            <button
              type="button"
              onClick={() => onGo(check)}
              /*
               * Nothing to go to on an `ok` row, and nothing on an `info`
               * one either: both are statements rather than jobs, and a row
               * that looks clickable and scrolls nowhere is worse than one
               * that does not.
               */
              disabled={check.level === 'ok' || check.level === 'info'}
              className="flex min-w-0 flex-1 items-start gap-2.5 text-left disabled:cursor-default"
            >
              <CheckDot level={check.level} />
              <span className={cn('min-w-0 flex-1 text-body',
                check.level === 'ok' || check.level === 'info'
                  ? 'text-text' : 'text-text hover:underline')}>
                {check.parts
                  ? check.parts.map((part, index) => (part.strong
                    ? <b key={index} className="font-semibold">{part.text}</b>
                    : <span key={index}>{part.text}</span>))
                  : check.label}
              </span>
            </button>
            {check.fix && (
              <button
                type="button"
                onClick={() => onGo(check)}
                className="shrink-0 whitespace-nowrap text-body font-semibold text-accent hover:text-accent-dark"
              >
                {FIX}
              </button>
            )}
            {onReview && check.key.startsWith('margin:') && check.visitId && (
              <button
                type="button"
                onClick={(event) => onReview(check.visitId!, event.currentTarget)}
                className="shrink-0 whitespace-nowrap text-body font-semibold text-accent hover:text-accent-dark"
              >
                {REVIEW}
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}

/**
 * 18px round, and only three shapes.
 *
 * A tick when it is fine, a triangle when it is not — and the triangle is the
 * same shape whether the row blocks or only warns, because the difference
 * between those two is what the Send button does, not how alarming a dot is.
 * Three levels of severity in a dot is three levels nobody can tell apart.
 *
 * `info` is the third shape and it is deliberately *not* a triangle: a grey
 * dot in a grey well, because a row saying "no logo is uploaded" wearing the
 * same amber warning as "this visit has no price" is how both stop being
 * read. Drawn explicitly rather than falling out of the `else`, which is what
 * `info` would otherwise have done.
 */
function CheckDot({ level }: { level: QuoteCheck['level'] }) {
  const ok = level === 'ok'
  const info = level === 'info'
  return (
    <span
      aria-hidden
      className={cn(
        'mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full',
        ok ? 'bg-accent-soft text-accent'
          : info ? 'bg-surface-alt text-text-faint' : 'bg-warn-bg text-warn',
      )}
    >
      {info
        ? <span className="h-1.5 w-1.5 rounded-full bg-text-faint" />
        : (
          <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor"
            strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
            {ok
              ? <path d="M5 13l4 4L19 7" />
              : <><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17h.01" /></>}
          </svg>
        )}
    </span>
  )
}

/**
 * The rail's card — 14px radius, one hairline, 18/16 padding.
 *
 * Exported because the package builder's "Try it" rail is the same surface
 * answering the same question, and Aaron asked for it in as many words: "That
 * whole section needs reformatted to the style, function and functionality of
 * the quote screens side preview bar." Two cards that are meant to look
 * identical and are defined twice are two cards that will stop looking
 * identical.
 */
export function RailCard({ children, internal, id }: {
  children: React.ReactNode
  /** Ours, not the customer's: the internal tint (qb-07 row 3). */
  internal?: boolean
  id?: string
}) {
  return (
    <section id={id} className={cn('rounded-record border border-line px-[18px] py-4',
      internal ? 'bg-surface-internal-card' : 'bg-surface')}>
      {children}
    </section>
  )
}

const Card = RailCard
const Line = RailLine

/**
 * One label-and-number row. Shared with the package builder's rail for the
 * same reason `RailCard` is.
 */
export function RailLine({
  label, value, strong, muted,
}: {
  label: string
  value: string
  strong?: boolean
  muted?: boolean
}) {
  return (
    <div className="flex items-baseline gap-2 py-px">
      <span className={cn('min-w-0 flex-1 truncate',
        strong ? 'text-[13.5px] font-bold text-text'
          : muted ? 'text-body text-text-muted' : 'text-body text-text')}>
        {label}
      </span>
      <span className={cn('tnum shrink-0',
        strong ? 'text-[13.5px] font-bold text-text'
          : muted ? 'text-body text-text-muted' : 'text-body text-text')}>
        {value}
      </span>
    </div>
  )
}
